import type { ReactNode } from "react";

import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
  Badge,
  Box,
  Button,
  Icon,
  cn,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";

import { makeFormItem, type FormItem, type FormStep } from "#app/lib/forms";

// Moves wait for the section like a card's quick actions wait for the card: shown while any of its cards is hovered or selected, always on touch.
export function SectionTab({
  index,
  total,
  onMove,
  revealed = false,
}: {
  index: number;
  total: number;
  onMove?: { up: SectionMove; down: SectionMove };
  revealed?: boolean;
}) {
  return (
    <Box align="center" direction="row" gap="2xs" paddingBottom="sm">
      <Badge color="primary" size="chip" tone="soft">
        Section {index + 1} of {total}
      </Badge>
      {onMove ? (
        <Box
          direction="row"
          gap="2xs"
          hoverReveal={revealed ? undefined : "section"}
          revealOnTouch
        >
          <MoveSectionButton direction="up" onClick={onMove.up} />
          <MoveSectionButton direction="down" onClick={onMove.down} />
        </Box>
      ) : null}
    </Box>
  );
}

type SectionMove = (() => void) | string | null;

function MoveSectionButton({
  direction,
  onClick,
}: {
  direction: "up" | "down";
  onClick: SectionMove;
}) {
  const label = direction === "up" ? "Move section up" : "Move section down";
  const blocked = typeof onClick === "string" ? onClick : null;
  const button = (
    <Button
      aria-label={label}
      disabled={typeof onClick !== "function"}
      onClick={typeof onClick === "function" ? onClick : undefined}
      size="icon-xs"
      variant="ghost"
    >
      <Icon color="muted" name={direction === "up" ? "arrow-up" : "arrow-down"} size="xs" />
    </Button>
  );
  return (
    <Tooltip>
      <TooltipTrigger render={blocked ? <Box as="span" /> : button}>{blocked ? button : undefined}</TooltipTrigger>
      <TooltipPopup>{blocked ?? label}</TooltipPopup>
    </Tooltip>
  );
}

const PAGE_LINE_OFFSET = 12;
const PAGE_LINE_REACH = 8;
const PAGE_LINE_TOP = 12;

// The wrapper is always rendered so a form gaining or losing pages never remounts the cards inside it.
export function PageLine({
  on,
  start = false,
  end = false,
  endGap = 0,
  onHover,
  children,
}: {
  on: boolean;
  start?: boolean;
  end?: boolean;
  endGap?: number;
  onHover?: (inside: boolean) => void;
  children: ReactNode;
}) {
  return (
    <Box
      direction="column"
      onPointerEnter={onHover ? () => onHover(true) : undefined}
      onPointerLeave={onHover ? () => onHover(false) : undefined}
      position="relative"
    >
      {on ? (
        <Box
          bottom={end ? endGap : 0}
          className={cn(
            "border-r-2 border-c-ink",
            start && "rounded-tr-md border-t-2",
            end && "rounded-br-md border-b-2",
          )}
          data-color="primary"
          opacity={40}
          pointerEvents="none"
          position="absolute"
          right={-PAGE_LINE_OFFSET}
          top={start ? PAGE_LINE_TOP : 0}
          width={PAGE_LINE_REACH}
        />
      ) : null}
      {children}
    </Box>
  );
}

export function SectionSplitHint({
  split,
  onInsert,
}: {
  split: { seat: number; step: FormStep };
  onInsert: (index: number, item: FormItem) => void;
}) {
  return (
    <Alert variant="info">
      <Icon name="heading-2" />
      <AlertTitle>{split.step.section ? "Long section? Split it here." : "Long form? Split it here."}</AlertTitle>
      <AlertDescription>Each section fills out as its own page.</AlertDescription>
      <AlertAction>
        <Button
          onClick={() => onInsert(split.seat, makeFormItem("section"))}
          size="sm"
          variant="outline"
        >
          Add section
        </Button>
      </AlertAction>
    </Alert>
  );
}
